import { Box, Button, Typography } from "@mui/material";
import { useCallback, useState } from "react";

import { ConfirmDeleteDialog } from "~ui/molecules/ConfirmDeleteDialog/ConfirmDeleteDialog";

import { useBulkRemoveUpcomingRsvps } from "../../hooks/useBulkRemoveUpcomingRsvps";

const tRemove = "Remove";
const tRsvpsSelected = (count: number) => `${count} RSVP${count === 1 ? "" : "s"} selected`;
const tAttendeesRemoved =
  "These attendees will be removed from the selected upcoming event times. They will be notified in-app.";

interface AdminUpcomingRsvpsBulkRemoveProps {
  bubbleId?: string;
  refetch?: () => void;
  selectedIds: string[];
  selectedLabels: string[];
  onCleared: () => void;
}

export function AdminUpcomingRsvpsBulkRemove({
  bubbleId,
  refetch,
  selectedIds,
  selectedLabels,
  onCleared,
}: AdminUpcomingRsvpsBulkRemoveProps) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const { isPending, removeRsvps } = useBulkRemoveUpcomingRsvps(bubbleId, refetch);

  const handleRemoveClick = useCallback(() => setDeleteDialogOpen(true), []);
  const handleDeleteCancel = useCallback(() => setDeleteDialogOpen(false), []);
  const handleDeleteConfirm = useCallback(() => {
    removeRsvps(selectedIds);
    onCleared();
    setDeleteDialogOpen(false);
  }, [onCleared, removeRsvps, selectedIds]);

  return (
    <>
      <Box sx={{ alignItems: "center", display: "flex", gap: 1 }}>
        {selectedIds.length > 0 && (
          <Typography color="text.secondary" variant="body2">
            {tRsvpsSelected(selectedIds.length)}
          </Typography>
        )}
        <Button
          color="error"
          disabled={selectedIds.length === 0 || !bubbleId || isPending}
          onClick={handleRemoveClick}
          size="small"
          variant="outlined"
        >
          {tRemove}
        </Button>
      </Box>
      <ConfirmDeleteDialog
        itemDescription={tAttendeesRemoved}
        itemName={selectedLabels}
        itemType={selectedIds.length === 1 ? "RSVP" : "RSVPs"}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        open={deleteDialogOpen}
      />
    </>
  );
}
